import { CongratsBooking } from '@client/pages/Storefront/components/CongratsBooking/CongratsBooking';
import { isInMcVrmPostBookingStorefrontExperiment } from '@client/redux/experiments/selectors/mcvrm-post-booking-storefront';
import { CATEGORY_SINGULAR_ABBREVIATED_TERMS } from '@constants/categories';
import { useIsStorefrontsNetworkEnabled } from '@hooks/use-storefronts-network';
import { useAppSelector } from '@redux/hooks';
import { CtaButton, Modal, TextBody, TextHero } from '@tkww/orion-web-ds';
import { Radio, SingleSelectionGroup } from '@tkww/orion-web-ds-input';
import React, {
	lazy,
	Suspense,
	useState,
	useCallback,
	useEffect,
	type FC,
	type MouseEvent,
	type KeyboardEvent,
} from 'react';
import { BookVendorSuccessModal } from './components/BookVendorSuccessModal';
import { BookVenueSuccessModal } from './components/BookVenueSuccessModal';
import Styles from './styles.scss';

const Mcvrm = lazy(() =>
	import('../../../lib/mcvrm').then((module) => ({ default: module.Mcvrm })),
);

type ModalContent = {
	title: string;
	getText: (vendorName: string, categoryName?: string) => React.ReactNode;
	confirmButtonText: string;
	cancelButtonText: string;
	foundOnTKText?: string;
};

type Props = {
	isVendorBooked?: boolean;
	content: ModalContent;
	onClose: () => void;
	onConfirm: (foundOnTheKnot?: string) => void;
	onCancel: () => void;
};

export const MarkAsBookedModal: FC<Props> = ({
	isVendorBooked = false,
	content,
	onClose,
	onConfirm,
	onCancel,
}) => {
	const vendor = useAppSelector((state) => state.vendor.vendor);
	const vendorRaw = useAppSelector((state) => state.vendor.vendorRaw);

	const [foundOnTheKnot, setFoundOnTheKnot] = useState<string | undefined>(
		undefined,
	);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [areActionsEnabled, setAreActionsEnabled] = useState(false);
	const isStorefrontsNetworkEnabled = useIsStorefrontsNetworkEnabled();

	const { title, getText, confirmButtonText, cancelButtonText, foundOnTKText } =
		content;

	const vendorCategoryName = vendor
		? vendor.categorySingularTerm ||
			CATEGORY_SINGULAR_ABBREVIATED_TERMS[vendor.categoryCode] ||
			vendor.categoryPluralTerm
		: 'vendor';

	const vendorName = vendor?.name || '';

	const inMcVrmPostBookingExperiment = useAppSelector(
		isInMcVrmPostBookingStorefrontExperiment,
	);

	useEffect(() => {
		setAreActionsEnabled(foundOnTKText ? foundOnTheKnot !== undefined : true);
	}, [foundOnTKText, foundOnTheKnot]);

	const handleConfirm = useCallback(() => {
		const foundOnTheKnotValue = foundOnTKText ? foundOnTheKnot : undefined;
		onConfirm(foundOnTheKnotValue);
		!isVendorBooked ? setShowSuccessModal(true) : onClose();
	}, [foundOnTKText, onConfirm, foundOnTheKnot, isVendorBooked, onClose]);

	const handleCancel = useCallback(() => {
		onCancel();
	}, [onCancel]);

	const handleCloseSuccessModal = useCallback(() => {
		setShowSuccessModal(false);
		onClose();
	}, [onClose]);

	const stopPropagation = useCallback(
		(e: MouseEvent<HTMLDivElement> | KeyboardEvent<HTMLDivElement>) => {
			e.stopPropagation();
		},
		[],
	);

	return (
		<>
			{showSuccessModal ? (
				<>
					{inMcVrmPostBookingExperiment && vendorRaw ? (
						<Suspense fallback={null}>
							<Mcvrm
								vendorRaw={vendorRaw}
								setModal={(modal) => {
									if (modal === null) handleCloseSuccessModal();
								}}
								startWithConfetti
								initialStep={
									<CongratsBooking vendorName={vendor?.name || ''} />
								}
								isPostBookingExperiment={inMcVrmPostBookingExperiment}
							/>
						</Suspense>
					) : isStorefrontsNetworkEnabled ? (
						<BookVenueSuccessModal
							isOpen={showSuccessModal}
							onClose={handleCloseSuccessModal}
						/>
					) : (
						<BookVendorSuccessModal
							isOpen={showSuccessModal}
							onClose={handleCloseSuccessModal}
						/>
					)}
				</>
			) : (
				<Modal size="sm" onClose={onClose}>
					<div
						className={`${Styles.container} ${foundOnTKText ? Styles.withSelection : ''}`}
						data-testid="storefront-mark-as-booked-modal"
					>
						<div className={Styles.contentWrapper}>
							<TextHero size="sm">{title}</TextHero>
							<TextBody size="md" className={Styles.text}>
								{getText(vendorName, vendorCategoryName)}
							</TextBody>
						</div>
						{foundOnTKText && (
							<div
								onClick={stopPropagation}
								onKeyDown={stopPropagation}
								className={Styles.selectionGroupWrapper}
							>
								<SingleSelectionGroup
									value={foundOnTheKnot}
									onValueChange={setFoundOnTheKnot}
								>
									<TextBody size="md">{foundOnTKText}</TextBody>
									<div className={Styles.selectionOptions}>
										<Radio value="Yes" data-testid="help-Yes">
											Yes
										</Radio>
										<Radio value="No" data-testid="help-No">
											No
										</Radio>
									</div>
								</SingleSelectionGroup>
							</div>
						)}
						<div className={Styles.actionsWrapper}>
							<div className={Styles.buttonsWrapper}>
								<CtaButton
									as="button"
									treatAs="button"
									styleType="primary"
									size="lg"
									onClick={handleConfirm}
									disabled={!areActionsEnabled}
									data-testid="storefront-mark-as-booked-confirm"
								>
									{confirmButtonText}
								</CtaButton>
								<CtaButton
									as="button"
									treatAs="button"
									styleType="secondary-alternative"
									size="lg"
									onClick={handleCancel}
									disabled={!areActionsEnabled}
									data-testid="storefront-mark-as-booked-cancel"
								>
									{cancelButtonText}
								</CtaButton>
							</div>
						</div>
					</div>
				</Modal>
			)}
		</>
	);
};
